<template>
  <div class="my-2">
    <!-- Question text -->
    <p
      v-if="question"
      class="mb-3 text-sm font-medium text-slate-700 dark:text-slate-200"
    >
      {{ question }}
    </p>

    <!-- Option buttons -->
    <div class="flex flex-col gap-2">
      <button
        v-for="(option, idx) in options"
        :key="idx"
        :disabled="isAnswered || isDisabled"
        :class="[
          'flex flex-col items-start rounded-xl border px-3.5 py-2.5 text-left text-sm transition-all',
          isAnswered && selectedOption === option.label
            ? 'border-indigo-400 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-900/40 dark:text-indigo-300'
            : isAnswered
              ? 'border-slate-100 bg-slate-50/50 text-slate-400 dark:border-slate-800 dark:bg-slate-800/30 dark:text-slate-600'
              : 'border-slate-200 bg-white text-slate-700 shadow-sm hover:border-indigo-300 hover:bg-indigo-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-indigo-500 dark:hover:bg-slate-700'
        ]"
        @click="selectOption(option.label)"
      >
        <span class="font-medium">{{ option.label }}</span>
        <span
          v-if="option.description"
          :class="[
            'mt-0.5 text-xs',
            isAnswered && selectedOption !== option.label
              ? 'text-slate-300 dark:text-slate-700'
              : 'text-slate-500 dark:text-slate-400'
          ]"
        >
          {{ option.description }}
        </span>
      </button>
    </div>

    <!-- Custom input (when allow_custom is true and not yet answered) -->
    <div
      v-if="allowCustom && !isAnswered"
      class="mt-2"
    >
      <div class="flex gap-2">
        <input
          v-model="customInput"
          type="text"
          placeholder="Or type something..."
          class="flex-1 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-700 placeholder-slate-400 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:placeholder-slate-500 dark:focus:border-indigo-500"
          :disabled="isDisabled"
          @keydown.enter="submitCustom"
        >
        <button
          v-if="customInput.trim()"
          class="rounded-xl bg-indigo-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-600"
          :disabled="isDisabled"
          @click="submitCustom"
        >
          Send
        </button>
      </div>
    </div>

    <!-- Answered indicator -->
    <div
      v-if="isAnswered && selectedOption && !options?.some(o => o.label === selectedOption)"
      class="mt-2 rounded-xl border border-indigo-200 bg-indigo-50 px-3.5 py-2 text-sm text-indigo-700 dark:border-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300"
    >
      {{ selectedOption }}
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  question?: string
  options?: Array<{ label: string; description?: string | null }>
  allowCustom?: boolean
  /** If set, shows the question as already answered */
  selectedOption?: string
  /** Disable interactions (e.g., while streaming) */
  isDisabled?: boolean
}>()

const emit = defineEmits<{
  select: [answer: string]
}>()

const customInput = ref('')

const isAnswered = computed(() => !!props.selectedOption)

function selectOption(label: string) {
  if (isAnswered.value || props.isDisabled) return
  emit('select', label)
}

function submitCustom() {
  const value = customInput.value.trim()
  if (!value || isAnswered.value || props.isDisabled) return
  emit('select', value)
  customInput.value = ''
}
</script>
